import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  X,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link, generatePath } from "react-router";

import { useTranslation } from "@punto/frontend-shared/i18n/i18next";
import { Button } from "@punto/ui/components/button";
import { cn } from "@punto/ui/lib/utils";

import { useBulkActions } from "./bulk-actions-context";
import { BulkAction, isBulkActionRunning } from "./types";

type IncidentOutcome = NonNullable<
  BulkAction["progress"]
>["incidents"][number]["outcome"];

/**
 * Group the retry-relevant incidents (`warning` + `failed`) by their processed
 * unit (`referenceId`), each with its sub-action items. Generic across bulk
 * action types — a unit is a certificate, a file, …
 */
const groupIncidents = (action: BulkAction) => {
  const groups = new Map<
    string,
    {
      label: string;
      items: { action: string; error?: string; outcome: IncidentOutcome }[];
    }
  >();
  for (const incident of action.progress?.incidents ?? []) {
    const existing = groups.get(incident.referenceId);
    const group = existing ?? {
      label: incident.referenceLabel ?? incident.referenceId,
      items: [],
    };
    group.items.push({
      action: incident.action,
      error: incident.error,
      outcome: incident.outcome,
    });
    if (!existing) groups.set(incident.referenceId, group);
  }
  return [...groups.entries()].map(([referenceId, group]) => ({
    referenceId,
    ...group,
  }));
};

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case "in_progress":
      return <Loader2 className="text-primary h-4 w-4 animate-spin" />;
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case "failed":
      return <XCircle className="text-destructive h-4 w-4" />;
    default:
      return <AlertTriangle className="text-muted-foreground h-4 w-4" />;
  }
};

/**
 * Per-bulk-action-type detail route for an incident's `referenceId`, supplied
 * by the host app. When a type is mapped, each incident group links to that
 * entity's detail page; otherwise the unit's label is plain text (e.g.
 * signed-import incidents reference a PDF file name, which isn't a linkable
 * entity — and Portus report downloads have no detail route at all).
 */
interface BulkActionsPanelProps {
  detailRouteByType?: Record<string, string>;
}

const BulkActionRow = ({
  action,
  detailRouteByType,
}: {
  action: BulkAction;
  detailRouteByType?: Record<string, string>;
}) => {
  const { t } = useTranslation();
  const { dismiss, handlersByType } = useBulkActions();
  // The type decides what following its link means (Janus marks a college
  // folder printed, P-3022); the panel only renders the link.
  const onDownload = handlersByType[action.type]?.onDownload;
  const [showErrors, setShowErrors] = useState(false);

  const types = t("BulkActionsPanel.types", {
    returnObjects: true,
  }) as Record<string, string>;
  const title = types[action.type] ?? t("BulkActionsPanel.default_title");
  // `returnObjects` on a missing key returns the key string, hence the guard:
  // an app without per-type labels (Portus) keeps the ZIP label.
  const downloadLabels = t("BulkActionsPanel.download_labels", {
    returnObjects: true,
  }) as Record<string, string> | string;
  const downloadLabel =
    (typeof downloadLabels === "object" && downloadLabels[action.type]) ||
    t("BulkActionsPanel.download_zip");
  const incidentActions = t("BulkActionsPanel.incident_actions", {
    returnObjects: true,
  }) as Record<string, string>;
  const warningMessages = t("BulkActionsPanel.warnings", {
    returnObjects: true,
  }) as Record<string, string>;

  const progress = action.progress;
  const total = progress?.total ?? 0;
  const processed = progress?.processed ?? 0;
  const succeeded = progress?.counts.succeeded ?? 0;
  const warning = progress?.counts.warning ?? 0;
  const failed = progress?.counts.failed ?? 0;
  const issues = warning + failed;
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
  const running = isBulkActionRunning(action);
  const incidentGroups = groupIncidents(action);
  const detailRoute = detailRouteByType?.[action.type];

  return (
    <li className="flex flex-col gap-1.5 px-4 py-3">
      <div className="flex items-center gap-2">
        <StatusIcon status={action.status} />
        <span className="flex-1 truncate text-sm font-medium">{title}</span>
        {!running && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            aria-label={t("BulkActionsPanel.dismiss")}
            onClick={() => dismiss(action.id)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            action.status === "failed" ? "bg-destructive" : "bg-primary",
          )}
          style={{ width: `${running ? percent : 100}%` }}
        />
      </div>

      <div className="text-muted-foreground flex items-center justify-between text-xs">
        <span>{t("BulkActionsPanel.progress", { processed, total })}</span>
        <span className="flex items-center gap-2">
          <span>{t("BulkActionsPanel.succeeded", { count: succeeded })}</span>
          {issues > 0 && (
            <span
              className={cn(failed > 0 ? "text-destructive" : "text-amber-600")}
            >
              {t("BulkActionsPanel.issues", { count: issues })}
            </span>
          )}
        </span>
      </div>

      {action.status === "completed" && action.downloadUrl && (
        <a
          href={action.downloadUrl}
          onClick={() => onDownload?.(action)}
          className="text-primary mt-0.5 flex items-center gap-1 text-xs font-medium underline underline-offset-4"
        >
          <Download className="h-3 w-3" />
          {downloadLabel}
        </a>
      )}

      {progress?.warnings?.map((warning) => (
        <output key={warning.code} className="text-warning block text-xs">
          {warningMessages[warning.code]}: {warning.labels.join(", ")}
        </output>
      ))}

      {incidentGroups.length > 0 && (
        <div className="mt-0.5">
          <button
            type="button"
            onClick={() => setShowErrors((prev) => !prev)}
            className={cn(
              "flex items-center gap-1 text-xs font-medium hover:underline",
              failed > 0 ? "text-destructive" : "text-amber-600",
            )}
            aria-expanded={showErrors}
          >
            {showErrors ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
            {t("BulkActionsPanel.incidents_toggle", {
              count: incidentGroups.length,
            })}
          </button>

          {showErrors && (
            <ul className="mt-1 max-h-40 space-y-1.5 overflow-y-auto pl-4 text-xs">
              {incidentGroups.map((group) => (
                <li key={group.referenceId}>
                  {detailRoute ? (
                    <Link
                      to={generatePath(detailRoute, { id: group.referenceId })}
                      className="text-primary font-medium underline underline-offset-4"
                    >
                      {group.label}
                    </Link>
                  ) : (
                    <span className="font-medium">{group.label}</span>
                  )}
                  <ul className="text-muted-foreground">
                    {group.items.map((item, index) => (
                      <li
                        key={`${item.action}-${index}`}
                        className={cn(
                          "break-words",
                          item.outcome === "failed" && "text-destructive",
                        )}
                      >
                        {incidentActions[item.action] ?? item.action}
                        {item.error
                          ? ` · ${incidentActions[item.error] ?? item.error}`
                          : ""}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

/**
 * Bottom-right "bulk actions" progress panel (Google-Drive style). Shows each
 * tracked bulk action (e.g. TRA050 regenerate) advancing live. Renders nothing
 * when no action is being tracked.
 */
export const BulkActionsPanel = ({
  detailRouteByType,
}: BulkActionsPanelProps) => {
  const { t } = useTranslation();
  const { actions, dismissAllFinished } = useBulkActions();
  const [collapsed, setCollapsed] = useState(false);

  if (actions.length === 0) return null;

  const runningCount = actions.filter(isBulkActionRunning).length;
  const hasFinished = actions.length > runningCount;

  return (
    <div className="bg-background fixed right-4 bottom-4 z-50 w-80 overflow-hidden rounded-lg border shadow-lg">
      <div className="bg-muted/50 flex items-center gap-2 border-b px-4 py-2">
        <span className="flex-1 truncate text-sm font-semibold">
          {runningCount > 0
            ? t("BulkActionsPanel.header_running", { count: runningCount })
            : t("BulkActionsPanel.header_done")}
        </span>
        {hasFinished && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            aria-label={t("BulkActionsPanel.dismiss_all")}
            onClick={dismissAllFinished}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          aria-label={t("BulkActionsPanel.toggle")}
          onClick={() => setCollapsed((prev) => !prev)}
        >
          {collapsed ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>

      {!collapsed && (
        <ul className="max-h-80 divide-y overflow-y-auto">
          {actions.map((action) => (
            <BulkActionRow
              key={action.id}
              action={action}
              detailRouteByType={detailRouteByType}
            />
          ))}
        </ul>
      )}
    </div>
  );
};
